import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { FileText, ImageIcon, Loader2, RefreshCw, Trash2, UploadCloud, AlertCircle, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export function useUploads(draftToken, cfg, err) {
  const [files, setFiles] = useState([]);
  const patch = (key, p) => setFiles((fs) => fs.map((f) => (f.key === key ? { ...f, ...p } : f)));
  const start = (file, key) => {
    const fd = new FormData();
    fd.append("draft_token", draftToken);
    fd.append("file", file);
    api.post("/uploads", fd, { onUploadProgress: (e) => patch(key, { progress: e.total ? Math.round((e.loaded / e.total) * 100) : 0 }) })
      .then((r) => patch(key, { status: "done", id: r.data?.id, progress: 100 }))
      .catch((e) => {
        const d = e?.response?.data?.detail;
        patch(key, { status: "error", error: e?.response?.status === 429 ? err.rate : err[d] || (e?.response?.status === 413 ? err.file_too_large : err.generic) });
      });
  };
  const add = (list, replaceKey = null) => {
    let active = files.filter((f) => f.status !== "error" && f.key !== replaceKey).length;
    const next = [];
    Array.from(list).forEach((file) => {
      const key = `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 8)}`;
      const ext = file.name.split(".").pop().toLowerCase();
      const base = { key, name: file.name, size: file.size, progress: 0 };
      if (!cfg.extensions.includes(ext)) next.push({ ...base, status: "error", error: err.unsupported_type });
      else if (file.size > cfg.max_mb * 1048576) next.push({ ...base, status: "error", error: err.file_too_large });
      else if (active >= cfg.max_files) next.push({ ...base, status: "error", error: err.too_many_files });
      else { active += 1; next.push({ ...base, status: "uploading" }); start(file, key); }
    });
    setFiles((fs) => {
      if (!replaceKey) return [...fs, ...next];
      const i = fs.findIndex((f) => f.key === replaceKey);
      const copy = fs.filter((f) => f.key !== replaceKey);
      copy.splice(i < 0 ? copy.length : i, 0, ...next);
      return copy;
    });
  };
  const remove = async (f) => {
    setFiles((fs) => fs.filter((x) => x.key !== f.key));
    if (f.id) api.delete(`/uploads/${f.id}`, { params: { draft_token: draftToken } }).catch(() => {});
  };
  const replace = (f, list) => { remove(f); add(list, f.key); };
  return { files, add, remove, replace };
}

const Row = ({ f, s, onRemove, onReplace }) => {
  const input = useRef(null);
  const Icon = /\.(png|jpe?g)$/i.test(f.name) ? ImageIcon : FileText;
  return (
    <motion.li layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className={`rounded-sm border bg-white p-4 ${f.status === "error" ? "border-[#E8A39B]" : "border-[var(--line)]"}`} data-testid="upload-file-row">
      <div className="flex items-center gap-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-[var(--paper-2)]"><Icon className="h-5 w-5 text-[var(--teal-ink)]" /></span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-[var(--ink)]" data-testid="upload-file-name">{f.name}</p>
          <p className="flex items-center gap-2 text-xs text-[var(--muted)]">
            {fmtSize(f.size)} ·
            {f.status === "uploading" && <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />{s.uploading} {f.progress}%</span>}
            {f.status === "done" && <span className="flex items-center gap-1 text-[var(--teal-ink)]"><CheckCircle2 className="h-3 w-3" />{s.done}</span>}
            {f.status === "error" && <span className="flex items-center gap-1 text-[#B42318]" data-testid="upload-file-error"><AlertCircle className="h-3 w-3" />{f.error}</span>}
          </p>
        </div>
        <input ref={input} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => { if (e.target.files?.length) onReplace(f, e.target.files); e.target.value = ""; }} />
        <button type="button" onClick={() => input.current?.click()} disabled={f.status === "uploading"} className="flex h-9 items-center gap-1.5 rounded-full border border-[var(--line)] px-3 text-xs text-[var(--ink)] hover:border-[var(--ink)] disabled:opacity-40" aria-label={s.replace} data-testid="upload-file-replace"><RefreshCw className="h-3.5 w-3.5" /><span className="hidden sm:inline">{s.replace}</span></button>
        <button type="button" onClick={() => onRemove(f)} disabled={f.status === "uploading"} className="flex h-9 items-center gap-1.5 rounded-full border border-[var(--line)] px-3 text-xs text-[var(--ink)] hover:border-[#B42318] hover:text-[#B42318] disabled:opacity-40" aria-label={s.remove} data-testid="upload-file-remove"><Trash2 className="h-3.5 w-3.5" /><span className="hidden sm:inline">{s.remove}</span></button>
      </div>
      {f.status === "uploading" && <div className="mt-3 h-1 overflow-hidden rounded-full bg-[var(--paper-2)]"><div className="h-full bg-[var(--teal)] transition-[width] duration-200" style={{ width: `${f.progress}%` }} data-testid="upload-progress-bar" /></div>}
    </motion.li>
  );
};

export const FileDrop = ({ s, cfg, up }) => {
  const [over, setOver] = useState(false);
  const input = useRef(null);
  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); up.add(e.dataTransfer.files); }}
        onClick={() => input.current?.click()}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); input.current?.click(); } }}
        role="button" tabIndex={0}
        className={`flex cursor-pointer flex-col items-center justify-center rounded-sm border-2 border-dashed px-6 py-14 text-center transition-[border-color,background-color] duration-300 ${over ? "border-[var(--teal-ink)] bg-[#DCEFEC]" : "border-[var(--line)] bg-white hover:border-[var(--teal)]"}`}
        data-testid="upload-dropzone"
      >
        <motion.span animate={over ? { y: -6, scale: 1.05 } : { y: 0, scale: 1 }} className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--ink)] text-white"><UploadCloud className="h-6 w-6" /></motion.span>
        <p className="mt-5 font-display text-xl text-[var(--ink)]">{s.drop}</p>
        <p className="mt-1 text-sm text-[var(--muted)]">{s.or} <span className="text-[var(--teal-ink)] underline underline-offset-2">{s.browse}</span></p>
        <p className="mt-4 font-mono text-xs text-[var(--muted)]" data-testid="upload-limits">{s.limits.replace("{n}", cfg.max_files).replace("{mb}", cfg.max_mb)}</p>
        <input ref={input} type="file" multiple className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,application/pdf,image/jpeg,image/png" onChange={(e) => { if (e.target.files?.length) up.add(e.target.files); e.target.value = ""; }} data-testid="upload-file-input" />
      </div>
      {up.files.length > 0 && <ul className="mt-5 space-y-3" data-testid="upload-file-list">{up.files.map((f) => <Row key={f.key} f={f} s={s} onRemove={up.remove} onReplace={up.replace} />)}</ul>}
    </div>
  );
};
